/**
 * DroppableChannelGrid - A droppable channel container with sortable image grid.
 *
 * Renders channel header with actions and a sortable grid of image cards.
 */

import {
  Globe,
  Radio,
  Sparkles,
  Settings,
  History,
  ShieldCheck,
  Plus,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useCallback } from 'react';
import { useChannelSlotMutation } from '@/hooks/useChannelSlotMutation';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import type { UniqueIdentifier } from '@dnd-kit/core';
import type { ProductAsset } from '@/types/products';
import { SortableImageCard } from './SortableImageCard';
import { ImageUploadSlot } from './ImageUploadSlot';
import { ChannelVersionInfo } from './ChannelVersionInfo';
import { ValidationBadge } from './ValidationBadge';
import { SyncStatusBadge } from './SyncStatusBadge';
import { Button, Badge } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import { useChannelValidation, getAssetErrors } from '@/hooks/useChannelValidation';
import type { ImageSize, PendingUploadResultForCallback } from './types';
import { IMAGE_SIZES } from './types';

export interface DroppableChannelGridProps {
  channelKey: string;
  channelName: string;
  isGlobal: boolean;
  assets: ProductAsset[];
  hasChanges: boolean;
  onImageClick: (asset: ProductAsset) => void;
  onInitialize: () => void;
  productId: string;
  channelId: string | null;
  onOpenGallery: (channelId: string, channelName: string) => void;
  onUploadSuccess: (channelId: string, channelKey: string, uploadResult?: PendingUploadResultForCallback) => void;
  onOptimizeImages: (channelId: string) => void;
  onViewSettings: (channelId: string, channelName: string) => void;
  onViewHistory: (channelId: string, channelName: string) => void;
  onViewRequirements: (channelId: string, channelName: string) => void;
  optimizingChannel: string | null;
  imageSize: ImageSize;
  activeId: UniqueIdentifier | null;
  isDropTarget: boolean;
  changedAssetIds: Set<string>;
  pendingUploadAssetIds: Set<string>;
  onDeleteSlot?: (channelId: string, linkId: string, slot: number) => void;
  isPendingDelete?: (channelId: string, linkId: string) => boolean;
  isCascadeDelete?: (channelId: string, linkId: string) => boolean;
  onUndoDelete?: (channelId: string, linkId: string) => void;
  getReplacement?: (channelId: string, oldLinkId: string) => { newAssetId: string } | undefined;
}

export function DroppableChannelGrid({
  channelKey,
  channelName,
  isGlobal,
  assets,
  hasChanges,
  onImageClick,
  onInitialize,
  productId,
  channelId,
  onOpenGallery,
  onUploadSuccess,
  onOptimizeImages,
  onViewSettings,
  onViewHistory,
  onViewRequirements,
  optimizingChannel,
  imageSize,
  activeId,
  isDropTarget,
  changedAssetIds,
  pendingUploadAssetIds,
  onDeleteSlot,
  isPendingDelete,
  isCascadeDelete,
  onUndoDelete,
  getReplacement,
}: DroppableChannelGridProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `channel-${channelKey}`,
    data: { type: 'channel', channelKey, channelId },
  });

  const showHighlight = (isDropTarget || isOver) && !isGlobal;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'channel-shelf rounded-lg border overflow-hidden transition-all duration-200',
        'bg-white dark:bg-neutral-900',
        'border-neutral-200 dark:border-neutral-700',
        showHighlight && 'ring-2 ring-primary-500 border-primary-500 dark:border-primary-400'
      )}
    >
      <ChannelHeader
        channelKey={channelKey}
        channelName={channelName}
        isGlobal={isGlobal}
        assets={assets}
        hasChanges={hasChanges}
        productId={productId}
        channelId={channelId}
        showHighlight={showHighlight}
        onOpenGallery={onOpenGallery}
        onOptimizeImages={onOptimizeImages}
        onViewSettings={onViewSettings}
        onViewHistory={onViewHistory}
        onViewRequirements={onViewRequirements}
        optimizingChannel={optimizingChannel}
      />
      <ChannelImageGrid
        channelKey={channelKey}
        channelName={channelName}
        assets={assets}
        productId={productId}
        channelId={channelId}
        onImageClick={onImageClick}
        onInitialize={onInitialize}
        onOpenGallery={onOpenGallery}
        onUploadSuccess={onUploadSuccess}
        imageSize={imageSize}
        activeId={activeId}
        changedAssetIds={changedAssetIds}
        pendingUploadAssetIds={pendingUploadAssetIds}
        onDeleteSlot={onDeleteSlot}
        isPendingDelete={isPendingDelete}
        isCascadeDelete={isCascadeDelete}
        onUndoDelete={onUndoDelete}
        getReplacement={getReplacement}
      />
    </div>
  );
}

function ChannelHeader({ channelKey, channelName, isGlobal, assets, hasChanges, productId, channelId, showHighlight, onOpenGallery, onOptimizeImages, onViewSettings, onViewHistory, onViewRequirements, optimizingChannel }: {
  channelKey: string;
  channelName: string;
  isGlobal: boolean;
  assets: ProductAsset[];
  hasChanges: boolean;
  productId: string;
  channelId: string | null;
  showHighlight: boolean;
  onOpenGallery: (channelId: string, channelName: string) => void;
  onOptimizeImages: (channelId: string) => void;
  onViewSettings: (channelId: string, channelName: string) => void;
  onViewHistory: (channelId: string, channelName: string) => void;
  onViewRequirements: (channelId: string, channelName: string) => void;
  optimizingChannel: string | null;
}) {
  return (
    <div className={cn(
      "flex items-center gap-3 px-4 py-3",
      "bg-neutral-50 dark:bg-neutral-800",
      "border-b border-neutral-200 dark:border-neutral-700"
    )}>
      <div className="flex items-center gap-3 flex-1">
        <ChannelIcon isGlobal={isGlobal} highlight={showHighlight} />
        <ChannelInfo
          channelKey={channelKey}
          channelName={channelName}
          isGlobal={isGlobal}
          assets={assets}
          productId={productId}
          channelId={channelId}
          showHighlight={showHighlight}
        />
        {hasChanges && (
          <Badge variant="warning" className="ml-2">
            Geändert
          </Badge>
        )}
      </div>
      {!isGlobal && channelId && (
        <ChannelActions
          channelId={channelId}
          channelName={channelName}
          onOpenGallery={onOpenGallery}
          onOptimizeImages={onOptimizeImages}
          onViewSettings={onViewSettings}
          onViewHistory={onViewHistory}
          onViewRequirements={onViewRequirements}
          optimizingChannel={optimizingChannel}
        />
      )}
    </div>
  );
}

function ChannelIcon({ isGlobal, highlight }: { isGlobal: boolean; highlight: boolean }) {
  if (isGlobal) {
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700">
        <Globe className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
      </div>
    );
  }
  return (
    <div className={cn(
      "flex items-center justify-center w-10 h-10 rounded-full",
      highlight ? 'bg-primary-200 dark:bg-primary-800' : 'bg-primary-100 dark:bg-primary-900'
    )}>
      <Radio className="h-5 w-5 text-primary-700 dark:text-primary-300" />
    </div>
  );
}

function ChannelInfo({ channelKey: _channelKey, channelName, isGlobal, assets, productId, channelId, showHighlight }: {
  channelKey: string;
  channelName: string;
  isGlobal: boolean;
  assets: ProductAsset[];
  productId: string;
  channelId: string | null;
  showHighlight: boolean;
}) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-50">
          {channelName}
        </h3>
        {showHighlight && !isGlobal && (
          <Badge variant="info" className="animate-pulse">Hier ablegen</Badge>
        )}
        {!isGlobal && channelId && !showHighlight && (
          <ValidationBadge productId={productId} channelId={channelId} />
        )}
        {!isGlobal && channelId && !showHighlight && (
          <SyncStatusBadge productId={productId} channelId={channelId} />
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
        {(() => {
          const count = assets.length;
          const maxSlot = Math.max(...assets.map(a => a.slot ?? a.sort_number ?? 0), 0);
          return (
            <span>
              {count} Bild{count !== 1 ? 'er' : ''}{maxSlot > 0 ? `, Slots 1–${maxSlot}` : ''}
            </span>
          );
        })()}
        {!isGlobal && channelId && (
          <ChannelVersionInfo productId={productId} channelId={channelId} />
        )}
      </div>
    </div>
  );
}

function ChannelActions({ channelId, channelName, onOpenGallery, onOptimizeImages, onViewSettings, onViewHistory, onViewRequirements, optimizingChannel }: {
  channelId: string;
  channelName: string;
  onOpenGallery: (channelId: string, channelName: string) => void;
  onOptimizeImages: (channelId: string) => void;
  onViewSettings: (channelId: string, channelName: string) => void;
  onViewHistory: (channelId: string, channelName: string) => void;
  onViewRequirements: (channelId: string, channelName: string) => void;
  optimizingChannel: string | null;
}) {
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="ghost" className="channel-action-add-btn" onClick={() => onOpenGallery(channelId, channelName)} title="Bild aus Library hinzufügen" aria-label="Bild aus Library hinzufügen">
        <Plus className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onViewRequirements(channelId, channelName)} title="Channel-Anforderungen konfigurieren">
        <ShieldCheck className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onViewSettings(channelId, channelName)} disabled={optimizingChannel === channelId} title="Einstellungen ansehen">
        <Settings className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="outline" onClick={() => onOptimizeImages(channelId)} disabled={optimizingChannel === channelId}>
        {optimizingChannel === channelId ? (
          <>
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2" />
            Optimiere...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-2" />
            Optimieren
          </>
        )}
      </Button>
      <Button size="sm" variant="outline" onClick={() => onViewHistory(channelId, channelName)} title="Versionshistorie ansehen">
        <History className="h-4 w-4 mr-2" />
        Historie
      </Button>
    </div>
  );
}

function DroppableEmptySlot({ channelKey, channelId, slot }: { channelKey: string; channelId: string | null; slot: number }) {
  const sortableId = `${channelKey}::empty-slot-${slot}`;
  const { setNodeRef, isOver } = useSortable({
    id: sortableId,
    data: { channelKey, channelId, slot, isEmpty: true },
    disabled: true,
  });

  return (
    <div
      ref={setNodeRef}
      className={`product-asset-empty-slot aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
        isOver
          ? 'border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-950/30'
          : 'border-neutral-200 dark:border-neutral-700'
      }`}
    >
      <span className={`text-xs font-medium ${isOver ? 'text-green-600 dark:text-green-400' : 'text-neutral-400 dark:text-neutral-500'}`}>
        Slot {slot}
      </span>
      <span className={`text-2xs ${isOver ? 'text-green-500 dark:text-green-500' : 'text-neutral-300 dark:text-neutral-600'}`}>
        {isOver ? 'Hier ablegen' : 'Leer'}
      </span>
    </div>
  );
}

/** Slot where an image was marked for deletion. Uses useDroppable (NOT sortable) so
 *  dropping an image here creates a replacement instead of triggering a reorder. */
function DroppableDeletedSlot({ channelKey, channelId, slot, oldAsset, replacementAssetId, onUndoDelete }: {
  channelKey: string;
  channelId: string | null;
  slot: number;
  oldAsset: ProductAsset;
  replacementAssetId?: string;
  onUndoDelete?: (channelId: string, linkId: string) => void;
}) {
  const droppableId = `${channelKey}::deleted-${oldAsset.link_id}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { channelKey, channelId, slot, isDeletedSlot: true, oldLinkId: oldAsset.link_id },
  });

  if (replacementAssetId) {
    return (
      <div ref={setNodeRef} className="relative group/card">
        <div className="ring-2 ring-dashed ring-emerald-500 rounded-lg">
          <img
            src={`/api/v1/asset/${replacementAssetId}/image`}
            alt=""
            className="aspect-square w-full rounded-lg object-cover"
          />
          <div className="absolute -top-2 -right-2 z-20 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-sm">
            Neu
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors relative group/del ${
        isOver
          ? 'border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-950/30'
          : 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/10'
      }`}
    >
      {channelId && onUndoDelete && (
        <button
          type="button"
          onClick={() => onUndoDelete(channelId, oldAsset.link_id)}
          className="absolute -top-2 -right-2 z-30 w-7 h-7 rounded-full bg-primary-500 hover:bg-primary-600 text-white flex items-center justify-center shadow-md opacity-0 group-hover/del:opacity-100 transition-opacity"
          title="Rückgängig"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </button>
      )}
      <Trash2 className={`h-5 w-5 ${isOver ? 'text-green-400' : 'text-red-400'}`} />
      <span className={`text-xs ${isOver ? 'text-green-600 dark:text-green-400' : 'text-red-400'}`}>
        {isOver ? 'Ersetzen' : 'Wird entfernt'}
      </span>
      <span className={`text-2xs ${isOver ? 'text-green-500' : 'text-red-300 dark:text-red-600'}`}>
        {isOver ? 'Hier ablegen' : 'Bild hier ablegen'}
      </span>
    </div>
  );
}

function ChannelImageGrid({ channelKey, channelName, assets, productId, channelId, onImageClick, onInitialize, onOpenGallery, onUploadSuccess, imageSize, activeId, changedAssetIds, pendingUploadAssetIds, onDeleteSlot, isPendingDelete, isCascadeDelete: _isCascadeDelete, onUndoDelete, getReplacement }: {
  channelKey: string;
  channelName: string;
  assets: ProductAsset[];
  productId: string;
  channelId: string | null;
  onImageClick: (asset: ProductAsset) => void;
  onInitialize: () => void;
  onOpenGallery: (channelId: string, channelName: string) => void;
  onUploadSuccess: (channelId: string, channelKey: string, uploadResult?: PendingUploadResultForCallback) => void;
  imageSize: ImageSize;
  activeId: UniqueIdentifier | null;
  changedAssetIds: Set<string>;
  pendingUploadAssetIds: Set<string>;
  onDeleteSlot?: (channelId: string, linkId: string, slot: number) => void;
  isPendingDelete?: (channelId: string, linkId: string) => boolean;
  isCascadeDelete?: (channelId: string, linkId: string) => boolean;
  onUndoDelete?: (channelId: string, linkId: string) => void;
  getReplacement?: (channelId: string, oldLinkId: string) => { newAssetId: string } | undefined;
}) {
  const { data: validation } = useChannelValidation(productId, channelId);
  const validationResults = validation?.validation_results;
  const { mutate: mutateSlot } = useChannelSlotMutation(productId);
  const handleAltTextGenerated = useCallback((assetId: string, text: string) => {
    if (!channelId) return;
    const asset = assets.find(a => a.asset_id === assetId);
    if (!asset) return;
    const slot = asset.slot ?? asset.sort_number;
    mutateSlot({ channelId, slot, asset_id: assetId, alt_text: text });
  }, [channelId, assets, mutateSlot]);

  // All assets stay in grid to preserve slot numbering (updateSlotNumbers counts them).
  // Pending-delete items are EXCLUDED from SortableContext (no reorder on drop)
  // but rendered as droppable replacement targets.
  const slotMap = new Map(assets.map(a => [a.slot ?? a.sort_number, a]));
  const occupiedSlots = assets.map(a => a.slot ?? a.sort_number ?? 0).filter(s => s > 0).sort((a, b) => a - b);
  const maxOccupied = occupiedSlots.length > 0 ? occupiedSlots[occupiedSlots.length - 1] : 0;
  const slots: Array<{ slot: number; asset: ProductAsset | null; isDeleted: boolean }> = [];
  let consecutiveEmpty = 0;
  for (let i = 1; i <= maxOccupied; i++) {
    const asset = slotMap.get(i) ?? null;
    const isDeleted = !!(asset && channelId && isPendingDelete?.(channelId, asset.link_id));
    if (asset) {
      consecutiveEmpty = 0;
      slots.push({ slot: i, asset, isDeleted });
    } else {
      consecutiveEmpty++;
      if (consecutiveEmpty <= 3) {
        slots.push({ slot: i, asset: null, isDeleted: false });
      }
    }
  }

  // SortableContext: only non-deleted real images (deleted ones use useDroppable separately)
  const sortableItems = slots
    .filter(s => s.asset !== null && !s.isDeleted)
    .map(s => `${channelKey}::${s.asset!.link_id}`);

  return (
    <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
      <div
        className="p-4 grid gap-3"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${IMAGE_SIZES[imageSize]}px, 1fr))` }}
      >
        {slots.map(({ slot, asset, isDeleted }) => {
          if (asset === null) {
            return (
              <DroppableEmptySlot key={`empty-${slot}`} channelKey={channelKey} channelId={channelId} slot={slot} />
            );
          }
          if (isDeleted) {
            const replacement = channelId ? getReplacement?.(channelId, asset.link_id) : undefined;
            return (
              <DroppableDeletedSlot
                key={`del-${asset.link_id}`}
                channelKey={channelKey}
                channelId={channelId}
                slot={slot}
                oldAsset={asset}
                replacementAssetId={replacement?.newAssetId}
                onUndoDelete={onUndoDelete}
              />
            );
          }
          const sortableId = `${channelKey}::${asset.link_id}`;
          return (
            <SortableImageCard
              key={sortableId}
              sortableId={sortableId}
              asset={asset}
              index={slot - 1}
              isDragging={activeId === sortableId}
              onClick={() => onImageClick(asset)}
              channelKey={channelKey}
              channelId={channelId}
              onInitialize={onInitialize}
              isChanged={changedAssetIds.has(sortableId)}
              isPendingUpload={pendingUploadAssetIds.has(asset.asset_id)}
              validationErrors={getAssetErrors(validationResults, asset.asset_id)}
              onDeleteSlot={onDeleteSlot}
              isPendingDelete={false}
              isCascadeDelete={false}
              onUndoDelete={onUndoDelete}
              productId={productId}
              onAltTextGenerated={handleAltTextGenerated}
            />
          );
        })}
        {channelId && (
          <ImageUploadSlot
            productId={productId}
            channelId={channelId}
            channelKey={channelKey}
            channelName={channelName}
            onUploadSuccess={onUploadSuccess}
            onOpenGallery={() => onOpenGallery(channelId, channelName)}
          />
        )}
      </div>
    </SortableContext>
  );
}


